import './signature-modal.scss';

import React from 'react';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import HelloModal from 'common/components/hello-modal';
import constants from 'common/signature-modal/constants';
import Button from 'hellospa/foundation/control/button';
import { withSignatureModalContext } from './contexts/signature-modal';

import TabBar from './tab-bar';
import Body from './body';

// Please note that if you change any localized message in
// this enture directory, it must be retranslated into
// Czech manually. This is because Czech is supported
// exlcusively in our Signer app for legacy purposes, but
// translations are not covered by Smartling.
const messages = defineMessages({
  insert: {
    id: '',
    description: 'Text for a button that inserts the users signature into a field.',
    defaultMessage: 'Insert',
  },
  insertEverywhere: {
    id: '',
    description: 'Text for a button that inserts the users signature into all fields field.',
    defaultMessage: 'Insert everywhere',
  },
  back: {
    id: '',
    description: 'Text for a button which closes a modal.',
    defaultMessage: 'Back',
  },
  addYourSignature: {
    id: '',
    description: 'Title for a modal where a user is able to type or draw in the signature that will appear on the document.',
    defaultMessage: 'Add your signature',
  },
  addYourInitials: {
    id: '',
    description: 'Title for a modal where a user is able to type or draw in their initials that will appear on the document.',
    defaultMessage: 'Add your initials',
  },
  footerDisclosureSignature: {
    id: '',
    description: 'Disclosure text that the user must agree to when signing a document.',
    defaultMessage: 'I understand this is a legal representation of my signature.',
  },
  footerDisclosureInitials: {
    id: '',
    description: 'Disclosure text that the user must agree to when signing a document.',
    defaultMessage: 'I understand this is a legal representation of my initials.',
  },
});

class SignatureModal extends React.PureComponent {

  static defaultProps = {
    isOpen: true,
    savedSignatures: [],
  };

  state = {
    enableInsert: false,
  };

  signatureModalBodyRef = React.createRef();

  enableInsertButtonCallback = (enableInsert) => {
    this.setState({
      enableInsert,
    });
  };

  onRequestClose = () => {
    this.props.clearUploadedSignature();

    // If the user was looking at the saved signatures tab
    // but did not have any saved signatures, then the next
    // time the user opens the signature modal we no
    // longer want to be on the saved signatures tab, so
    // instead we'll change to the draw tab.
    if (this.props.selectedTab === constants.TAB_SAVED && this.props.savedSignatures.length === 0) {
      this.props.onTabSelect(constants.TAB_DRAW);
    }

    this.props.onClose();
  };

  getSignatureData() {
    const data = this.signatureModalBodyRef.current.getSignatureData();

    if (this.props.selectedTab === constants.TAB_SAVED) {
      return data;
    } else {
      return {
        ...data,
        type_code: this.props.type,
      };
    }
  }

  uploadSignature = (...args) => {
    this.props.onUploadSignature(...args, this.props.type);
  };

  insertSignature = (insertEverywhere = false) => {
    const signatureData = this.getSignatureData();

    if (this.props.selectedTab === constants.TAB_SAVED) {
      this.props.onInsert(signatureData, insertEverywhere);
    } else {
      this.props.onSignatureData(signatureData, insertEverywhere);
    }
  };

  insertSignatureEverywhere = () => {
    this.insertSignature(true);
  };

  renderContentLabel() {
    const { intl, type } = this.props;

    switch (type) {
      case constants.TYPE_SIGNATURE:
        return intl.formatMessage(messages.addYourSignature);
      case constants.TYPE_INITIALS:
        return intl.formatMessage(messages.addYourInitials);
      default:
        throw new TypeError('Invalid type');
    }
  }

  renderFooterDisclosure() {
    const { intl, type } = this.props;

    switch (type) {
      case constants.TYPE_SIGNATURE:
        return intl.formatMessage(messages.footerDisclosureSignature);
      case constants.TYPE_INITIALS:
        return intl.formatMessage(messages.footerDisclosureInitials);
      default:
        throw new TypeError('Invalid type');
    }
  }

  renderButtons() {
    const { intl, buttonText, canInsertEverywhere } = this.props;
    const { enableInsert } = this.state;

    const buttons = [
      <Button
        data-qa-ref='singing-modal--insert-btn'
        className="whitelabel-primary-button"
        disabled={this.props.loading || !enableInsert}
        onClick={() => this.insertSignature()}
        key="insert"
      >
        {!!buttonText && !canInsertEverywhere
          ? buttonText
          : intl.formatMessage(messages.insert)
        }
      </Button>,
    ];

    if (canInsertEverywhere) {
      buttons.push(
        <Button
          className="whitelabel-primary-button"
          onClick={() => this.insertSignatureEverywhere()}
          disabled={this.props.loading || !enableInsert}
        >
          { intl.formatMessage(messages.insertEverywhere) }
        </Button>,
      );
    }

    return buttons;
  }

  render() {
    const {
      isOpen,
      isDropbox,
      isEmbedded,
      onTabSelect,
      selectedTab,
      savedSignatures,
      loading,
      enabledTabs,
      getSignatureUrl,
      onSavedSignatureSelect,
      onSavedSignatureRemove,
      selectedSavedSignature,
      isUploadingSignature,
      uploadedSignature,
      onRotateSignature,
      clearUploadedSignature,
      initialTypeInValue,
      type,
      getPaginatedSignatures,
      getPaginatedInitials,
      initialsCount,
      signaturesCount,
    } = this.props;
    const count = (type === constants.TYPE_SIGNATURE) ? signaturesCount : initialsCount;
    return (
      <HelloModal
        isOpen={isOpen}
        onRequestClose={this.onRequestClose}
        contentLabel={this.renderContentLabel()}
        buttons={this.renderButtons()}
        extraClasses="signature-modal"
        contentClasses="hello-modal__content--signature-modal"
        footerDisclosure={this.renderFooterDisclosure()}
        bodyOpenClassName="signature-modal-open"
      >
        {(loading && selectedTab !== constants.TAB_UPLOAD) ? (
          <p className="signature-modal__loading">
            <FormattedMessage
              id=""
              description="Loading message"
              defaultMessage="Loading…" />
          </p>
        ) : (
          <React.Fragment>
            <TabBar
              onTabSelect={onTabSelect}
              selectedTab={selectedTab}
              isDropbox={isDropbox}
              isEmbedded={isEmbedded}
              signatures={savedSignatures}
              enabledTabs={enabledTabs} />
            <Body
              ref={this.signatureModalBodyRef}
              selectedTab={selectedTab}
              signatures={savedSignatures}
              getSignatureUrl={getSignatureUrl}
              onSavedSignatureSelect={onSavedSignatureSelect}
              selectedSavedSignature={selectedSavedSignature}
              onSavedSignatureRemove={onSavedSignatureRemove}
              isUploadingSignature={isUploadingSignature}
              uploadedSignature={uploadedSignature}
              onRotateSignature={onRotateSignature}
              clearUploadedSignature={clearUploadedSignature}
              uploadSignature={this.uploadSignature}
              enableInsertButtonCallback={this.enableInsertButtonCallback}
              initialTypeInValue={initialTypeInValue}
              type={type}
              getPaginatedSignatures={getPaginatedSignatures}
              getPaginatedInitials={getPaginatedInitials}
              count={count} />
          </React.Fragment>
        )}
      </HelloModal>
    );
  }
}

export default withSignatureModalContext(injectIntl(SignatureModal));
